import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { itemsAPI, ordersAPI, dispatchAPI } from '@/lib/api';
import { getToday } from '@/lib/utils';
import {
  Package,
  AlertTriangle,
  ShoppingCart,
  TrendingUp,
  CheckCircle,
  BarChart3,
  Calendar,
  MapPin,
  Target,
  PieChart,
  Filter,
  Percent,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  Area,
  AreaChart,
  CartesianGrid
} from 'recharts';

// Colores para cada local
const LOCATION_COLORS = [
  { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-700' },
  { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-700' },
  { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-700' },
  { bg: 'bg-pink-500', light: 'bg-pink-100', text: 'text-pink-700' },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStockItems: 0,
    ordersToday: 0,
    submittedToday: 0
  });
  const [lowStockItems, setLowStockItems] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topByLocation, setTopByLocation] = useState([]);
  const [ordersByPeriod, setOrdersByPeriod] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [loadingPeriod, setLoadingPeriod] = useState(false);
  
  // Estados para estadísticas de cumplimiento
  const [fulfillmentStats, setFulfillmentStats] = useState(null);
  const [loadingFulfillment, setLoadingFulfillment] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [fulfillmentPeriod, setFulfillmentPeriod] = useState('30');

  // Estados para análisis de productos por período
  const [productsPeriod, setProductsPeriod] = useState('week');
  const [productsStats, setProductsStats] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [orderTrends, setOrderTrends] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);

  // Colores para gráficos de cumplimiento
  const FULFILLMENT_COLORS = ['#22c55e', '#f59e0b', '#ef4444']; // verde, amarillo, rojo
  const BAR_COLORS = ['#E07B54', '#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b'];
  const CHART_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    loadPeriodData();
  }, [selectedPeriod]);

  useEffect(() => {
    loadFulfillmentData();
  }, [selectedSupplier, fulfillmentPeriod]);

  useEffect(() => {
    loadProductsStats();
  }, [productsPeriod]);

  useEffect(() => {
    loadOrderTrends();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [itemsRes, lowStockRes, ordersRes, topProductsRes, topByLocationRes] = await Promise.all([
        itemsAPI.getAll(true),
        itemsAPI.getLowStock(),
        ordersAPI.getByDate(getToday()),
        ordersAPI.getTopProducts(10),
        ordersAPI.getTopByLocation(10)
      ]);

      setStats({
        totalItems: itemsRes.data.length,
        lowStockItems: lowStockRes.data.length,
        ordersToday: ordersRes.data.length,
        submittedToday: ordersRes.data.filter(o => o.status === 'submitted').length
      });

      setLowStockItems(lowStockRes.data.slice(0, 5));
      setTopProducts(topProductsRes.data || []);
      setTopByLocation(topByLocationRes.data || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPeriodData = async () => {
    setLoadingPeriod(true);
    try {
      const res = await ordersAPI.getByLocationPeriod(selectedPeriod);
      setOrdersByPeriod(res.data || []);
    } catch (error) {
      console.error('Error loading period data:', error);
    } finally {
      setLoadingPeriod(false);
    }
  };

  const loadFulfillmentData = async () => {
    setLoadingFulfillment(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(fulfillmentPeriod) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const params = {
        startDate,
        endDate,
        supplier: selectedSupplier !== 'all' ? selectedSupplier : null
      };
      
      const res = await dispatchAPI.getFulfillmentStats(params);
      setFulfillmentStats(res.data);
    } catch (error) {
      console.error('Error loading fulfillment stats:', error);
    } finally {
      setLoadingFulfillment(false);
    }
  };

  const loadProductsStats = async () => {
    setLoadingProducts(true);
    try {
      const res = await ordersAPI.getProductsStatsByPeriod(productsPeriod, null, null, 15);
      setProductsStats(res.data);
    } catch (error) {
      console.error('Error loading products stats:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadOrderTrends = async () => {
    setLoadingTrends(true);
    try {
      const res = await ordersAPI.getOrderTrends(30);
      setOrderTrends(res.data);
    } catch (error) {
      console.error('Error loading order trends:', error);
    } finally {
      setLoadingTrends(false);
    }
  };

  // Find max quantity for chart scaling
  const maxQuantity = topProducts.length > 0 
    ? Math.max(...topProducts.map(p => p.total_quantity))
    : 0;

  // Max for period chart - memoizado para rendimiento
  const maxPeriodTotal = useMemo(() => {
    return ordersByPeriod.length > 0
      ? Math.max(...ordersByPeriod.map(l => l.total))
      : 0;
  }, [ordersByPeriod]);
  
  // Total de pedidos por período (memoizado)
  const periodTotal = useMemo(() => {
    return ordersByPeriod.reduce((sum, l) => sum + l.total, 0).toFixed(1);
  }, [ordersByPeriod]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Productos Activos',
      value: stats.totalItems,
      icon: Package,
      bgColor: 'bg-white',
      borderColor: 'border-[#E9E4DA]',
      iconBgColor: 'bg-[#FFF1E6]',
      iconColor: 'text-[#F97316]',
      textColor: 'text-[#153D35]'
    },
    {
      title: 'Stock Bajo',
      value: stats.lowStockItems,
      icon: AlertTriangle,
      bgColor: 'bg-white',
      borderColor: 'border-[#E9E4DA]',
      iconBgColor: 'bg-red-50',
      iconColor: 'text-red-500',
      textColor: 'text-[#153D35]'
    },
    {
      title: 'Pedidos Hoy',
      value: stats.ordersToday,
      icon: ShoppingCart,
      bgColor: 'bg-white',
      borderColor: 'border-[#E9E4DA]',
      iconBgColor: 'bg-green-50',
      iconColor: 'text-green-500',
      textColor: 'text-[#153D35]'
    },
    {
      title: 'Enviados Hoy',
      value: stats.submittedToday,
      icon: CheckCircle,
      bgColor: 'bg-white',
      borderColor: 'border-[#E9E4DA]',
      iconBgColor: 'bg-[#FFF8D6]',
      iconColor: 'text-[#CA8A04]',
      textColor: 'text-[#153D35]'
    }
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#153D35' }}>Dashboard</h1>
        <p className="mt-1" style={{ color: '#68756F' }}>Resumen general del sistema</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${stat.bgColor} rounded-lg border ${stat.borderColor} p-6 shadow-sm`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: '#68756F' }}>{stat.title}</p>
                  <p className={`text-3xl font-bold mt-2 ${stat.textColor}`}>{stat.value}</p>
                </div>
                <div className={`w-12 h-12 ${stat.iconBgColor} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Sección de Cumplimiento de Pedidos */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-primary" />
            <div>
              <h2 className="text-lg font-heading font-bold text-slate-900">
                Cumplimiento de Pedidos
              </h2>
              <p className="text-sm text-muted-foreground">Análisis de entregas vs solicitado</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Select value={fulfillmentPeriod} onValueChange={setFulfillmentPeriod}>
              <SelectTrigger className="w-36" data-testid="fulfillment-period-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 días</SelectItem>
                <SelectItem value="30">Últimos 30 días</SelectItem>
                <SelectItem value="90">Últimos 90 días</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
              <SelectTrigger className="w-44" data-testid="fulfillment-supplier-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {fulfillmentStats?.available_suppliers?.map(supplier => (
                  <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="p-6">
          {loadingFulfillment ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !fulfillmentStats || fulfillmentStats.summary.total_orders === 0 ? (
            <div className="text-center py-12">
              <PieChart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No hay datos de despachos en el período seleccionado
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Los datos se generan cuando se registran despachos de pedidos
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Resumen Global */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-slate-900">{fulfillmentStats.summary.total_orders}</p>
                  <p className="text-sm text-muted-foreground">Pedidos Despachados</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{fulfillmentStats.summary.global_fulfillment_rate}%</p>
                  <p className="text-sm text-muted-foreground">Cumplimiento Global</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{fulfillmentStats.summary.full_order_rate}%</p>
                  <p className="text-sm text-muted-foreground">Pedidos Completos</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">
                    {fulfillmentStats.summary.total_items_dispatched.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">Unidades Enviadas</p>
                </div>
              </div>
              
              {/* 3 Gráficos */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Gráfico 1: Distribución de Estados de Pedidos (Pie Chart) */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <PieChart className="w-4 h-4" />
                    Estado de Pedidos
                  </h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={[
                            { name: 'Completos', value: fulfillmentStats.summary.fully_fulfilled_orders },
                            { name: 'Parciales', value: fulfillmentStats.summary.partially_fulfilled_orders },
                            { name: 'Sin envío', value: fulfillmentStats.summary.not_fulfilled_orders }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {FULFILLMENT_COLORS.map((color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value} pedidos`, '']} />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          formatter={(value, entry) => (
                            <span className="text-xs text-slate-600">{value}</span>
                          )}
                        />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Gráfico 2: Cumplimiento por Local (Bar Chart) */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Cumplimiento por Local
                  </h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={fulfillmentStats.by_location.slice(0, 5)}
                        layout="vertical"
                        margin={{ top: 0, right: 10, left: 0, bottom: 0 }}
                      >
                        <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} fontSize={10} />
                        <YAxis 
                          type="category" 
                          dataKey="location_name" 
                          width={80} 
                          fontSize={10}
                          tickFormatter={(v) => v.length > 12 ? v.slice(0, 12) + '...' : v}
                        />
                        <Tooltip 
                          formatter={(value) => [`${value}%`, 'Cumplimiento']}
                          labelFormatter={(label) => `Local: ${label}`}
                        />
                        <Bar dataKey="fulfillment_rate" fill="#E07B54" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Gráfico 3: Productos con Menor Cumplimiento */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Percent className="w-4 h-4" />
                    Productos con Menor Cumplimiento
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {fulfillmentStats.by_product.slice(0, 8).map((product, index) => (
                      <div key={product.item_id} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">{product.item_name}</p>
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden mt-1">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{ 
                                width: `${product.fulfillment_rate}%`,
                                backgroundColor: product.fulfillment_rate >= 80 ? '#22c55e' : 
                                                product.fulfillment_rate >= 50 ? '#f59e0b' : '#ef4444'
                              }}
                            />
                          </div>
                        </div>
                        <span className={`text-xs font-bold min-w-[40px] text-right ${
                          product.fulfillment_rate >= 80 ? 'text-green-600' : 
                          product.fulfillment_rate >= 50 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {product.fulfillment_rate}%
                        </span>
                      </div>
                    ))}
                    {fulfillmentStats.by_product.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-4">
                        No hay datos de productos
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Tabla detallada por local */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Detalle por Local</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-semibold text-slate-600">Local</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">Pedidos</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">Completos</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">Parciales</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">Sin envío</th>
                        <th className="text-center py-2 px-3 font-semibold text-slate-600">% Cumplimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fulfillmentStats.by_location.map((location, index) => (
                        <tr key={location.location_name} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium text-slate-900">{location.location_name}</td>
                          <td className="py-2 px-3 text-center">{location.total_orders}</td>
                          <td className="py-2 px-3 text-center text-green-600">{location.fully_fulfilled}</td>
                          <td className="py-2 px-3 text-center text-amber-600">{location.partially_fulfilled}</td>
                          <td className="py-2 px-3 text-center text-red-600">{location.not_fulfilled}</td>
                          <td className="py-2 px-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                              location.fulfillment_rate >= 80 ? 'bg-green-100 text-green-700' :
                              location.fulfillment_rate >= 50 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {location.fulfillment_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gráfico de Pedidos por Local - Semana/Mes */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-heading font-bold text-slate-900">
              Pedidos por Local
            </h2>
          </div>
          <div className="flex gap-2">
            <Button
              variant={selectedPeriod === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPeriod('week')}
            >
              <Calendar className="w-4 h-4 mr-1" />
              Semana
            </Button>
            <Button
              variant={selectedPeriod === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPeriod('month')}
            >
              <Calendar className="w-4 h-4 mr-1" />
              Mes
            </Button>
          </div>
        </div>
        <div className="p-6">
          {loadingPeriod ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : ordersByPeriod.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No hay datos de pedidos en {selectedPeriod === 'week' ? 'la última semana' : 'el último mes'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Barras horizontales por local */}
              <div className="space-y-4">
                {ordersByPeriod.map((location, index) => {
                  const color = LOCATION_COLORS[index % LOCATION_COLORS.length];
                  const percentage = maxPeriodTotal > 0 ? (location.total / maxPeriodTotal) * 100 : 0;
                  
                  return (
                    <motion.div
                      key={location.location_name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${color.bg}`}></div>
                          <span className="font-medium text-slate-900">{location.location_name}</span>
                        </div>
                        <span className="text-lg font-bold text-slate-900">
                          {location.total.toFixed(1)} <span className="text-sm font-normal text-muted-foreground">unidades</span>
                        </span>
                      </div>
                      <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          className={`h-full ${color.bg} rounded-full`}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Resumen de totales */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Período: {selectedPeriod === 'week' ? 'Últimos 7 días' : 'Últimos 30 días'}
                  </span>
                  <span className="font-semibold text-slate-900">
                    Total: {periodTotal} unidades
                  </span>
                </div>
              </div>

              {/* Mini gráfico de tendencia por día si hay datos */}
              {ordersByPeriod.length > 0 && ordersByPeriod[0].data && (
                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Tendencia diaria</h3>
                  <div className="flex items-end gap-1 h-20">
                    {ordersByPeriod[0].data.slice(-14).map((day, idx) => {
                      // Sum all locations for this day
                      const dayTotal = ordersByPeriod.reduce((sum, loc) => {
                        const locDay = loc.data.find(d => d.date === day.date);
                        return sum + (locDay ? locDay.total_quantity : 0);
                      }, 0);
                      const maxDayTotal = Math.max(...ordersByPeriod[0].data.map((_, i) => 
                        ordersByPeriod.reduce((sum, loc) => sum + (loc.data[i]?.total_quantity || 0), 0)
                      ));
                      const height = maxDayTotal > 0 ? (dayTotal / maxDayTotal) * 100 : 0;
                      
                      return (
                        <div
                          key={day.date}
                          className="flex-1 flex flex-col items-center"
                          title={`${day.date}: ${dayTotal.toFixed(1)} unidades`}
                        >
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: `${Math.max(height, 4)}%` }}
                            transition={{ duration: 0.3, delay: idx * 0.02 }}
                            className={`w-full rounded-t ${dayTotal > 0 ? 'bg-primary' : 'bg-slate-200'}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-muted-foreground">
                      {ordersByPeriod[0].data.slice(-14)[0]?.date?.slice(5)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {ordersByPeriod[0].data.slice(-1)[0]?.date?.slice(5)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 10 Productos más pedidos */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm lg:col-span-2">
          <div className="p-6 border-b border-slate-200 flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-heading font-bold text-slate-900">Top 10 Productos Más Pedidos</h2>
          </div>
          <div className="p-6">
            {topProducts.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No hay datos de pedidos aún</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topProducts.map((product, index) => (
                  <div key={product.item_id} className="flex items-center gap-4">
                    <span className="w-6 text-sm font-bold text-slate-500">#{index + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-900 truncate">{product.item_name}</span>
                        <span className="text-sm font-bold text-primary">{product.total_quantity} {product.unit}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(product.total_quantity / maxQuantity) * 100}%` }}
                          transition={{ duration: 0.5, delay: index * 0.05 }}
                          className="h-full bg-gradient-to-r from-primary to-blue-500 rounded-full"
                        />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-16 text-right">{product.order_count} pedidos</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Productos por Local */}
        {topByLocation.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm lg:col-span-2">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-heading font-bold text-slate-900">Productos Más Pedidos por Local</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {topByLocation.map((location) => (
                  <div key={location.location_name} className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-200">
                      {location.location_name}
                    </h3>
                    <div className="space-y-2">
                      {location.products.slice(0, 5).map((product, idx) => (
                        <div key={product.item_id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-700 truncate flex-1">
                            <span className="text-slate-400 mr-2">{idx + 1}.</span>
                            {product.item_name}
                          </span>
                          <span className="font-medium text-slate-900 ml-2">{product.total_quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-lg font-heading font-bold text-slate-900">Productos con Stock Bajo</h2>
          </div>
          <div className="p-6">
            {lowStockItems.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Todos los productos tienen stock suficiente</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lowStockItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-md"
                  >
                    <div>
                      <p className="font-semibold text-slate-900">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.code}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-red-700">{item.current_stock} {item.unit}</p>
                      <p className="text-xs text-muted-foreground">Mín: {item.minimum_stock}</p>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => navigate('/admin/inventory')}
                >
                  Ver todo el inventario
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-lg font-heading font-bold text-slate-900">Acciones Rápidas</h2>
          </div>
          <div className="p-6 space-y-3">
            <Button
              className="w-full justify-start h-auto py-4"
              variant="outline"
              onClick={() => navigate('/admin/consolidated')}
            >
              <TrendingUp className="w-5 h-5 mr-3" />
              <div className="text-left">
                <p className="font-semibold">Ver consolidado del día</p>
                <p className="text-xs text-muted-foreground">Pedido final calculado</p>
              </div>
            </Button>

            <Button
              className="w-full justify-start h-auto py-4"
              variant="outline"
              onClick={() => navigate('/admin/orders')}
            >
              <ShoppingCart className="w-5 h-5 mr-3" />
              <div className="text-left">
                <p className="font-semibold">Pedidos por local</p>
                <p className="text-xs text-muted-foreground">Revisar pedidos de hoy</p>
              </div>
            </Button>

            <Button
              className="w-full justify-start h-auto py-4"
              variant="outline"
              onClick={() => navigate('/admin/import')}
            >
              <Package className="w-5 h-5 mr-3" />
              <div className="text-left">
                <p className="font-semibold">Importar productos</p>
                <p className="text-xs text-muted-foreground">Carga masiva por Excel</p>
              </div>
            </Button>
          </div>
        </div>
      </div>

      {/* Nueva Sección: Análisis de Productos por Período */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-primary" />
            <div>
              <h2 className="text-lg font-heading font-bold text-slate-900">
                Análisis de Productos Pedidos
              </h2>
              <p className="text-sm text-muted-foreground">Productos más solicitados por los locales</p>
            </div>
          </div>
          <Select value={productsPeriod} onValueChange={setProductsPeriod}>
            <SelectTrigger className="w-40" data-testid="products-period-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Última semana</SelectItem>
              <SelectItem value="month">Último mes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="p-6">
          {loadingProducts ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !productsStats || productsStats.summary.total_products === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No hay pedidos en el período seleccionado
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Resumen del período */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{productsStats.summary.total_orders}</p>
                  <p className="text-xs text-muted-foreground">Pedidos</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{productsStats.summary.total_products}</p>
                  <p className="text-xs text-muted-foreground">Productos únicos</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    {productsStats.summary.total_quantity.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">Unidades totales</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{productsStats.summary.total_locations}</p>
                  <p className="text-xs text-muted-foreground">Locales activos</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-slate-700">
                    {productsStats.summary.avg_daily_quantity}
                  </p>
                  <p className="text-xs text-muted-foreground">Prom. diario</p>
                </div>
              </div>

              {/* Gráficos en Tabs */}
              <Tabs defaultValue="top-products" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="top-products">Top Productos</TabsTrigger>
                  <TabsTrigger value="by-location">Por Local</TabsTrigger>
                  <TabsTrigger value="trend">Tendencia</TabsTrigger>
                </TabsList>

                {/* Tab: Top Productos */}
                <TabsContent value="top-products" className="mt-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Gráfico de barras */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-slate-900 mb-4">Top 10 Productos Más Pedidos</h3>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={productsStats.top_products.slice(0, 10)}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis 
                              type="category" 
                              dataKey="item_name" 
                              tick={{ fontSize: 11 }}
                              width={95}
                            />
                            <Tooltip 
                              formatter={(value, name) => [`${value} ${productsStats.top_products[0]?.unit || 'unidades'}`, 'Cantidad']}
                            />
                            <Bar dataKey="total_quantity" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Lista con detalles */}
                    <div className="bg-slate-50 rounded-lg p-4 overflow-hidden">
                      <h3 className="text-sm font-semibold text-slate-900 mb-4">Desglose por Local</h3>
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {productsStats.top_products.slice(0, 8).map((product, idx) => (
                          <div key={product.item_id} className="bg-white rounded-lg p-3 border border-slate-200">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
                                  {idx + 1}
                                </span>
                                <span className="font-medium text-sm truncate max-w-[150px]">{product.item_name}</span>
                              </div>
                              <span className="font-bold text-blue-600">{product.total_quantity} {product.unit}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(product.locations || {}).slice(0, 4).map(([loc, qty]) => (
                                <span key={loc} className="text-xs bg-slate-100 px-2 py-1 rounded">
                                  {loc}: {qty}
                                </span>
                              ))}
                              {Object.keys(product.locations || {}).length > 4 && (
                                <span className="text-xs text-slate-500">
                                  +{Object.keys(product.locations).length - 4} más
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab: Por Local */}
                <TabsContent value="by-location" className="mt-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Gráfico de pie */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-slate-900 mb-4">Distribución por Local</h3>
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPie>
                            <Pie
                              data={productsStats.by_location.slice(0, 7).map((loc, idx) => ({
                                name: loc.location_name,
                                value: loc.total_quantity
                              }))}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={90}
                              paddingAngle={2}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              labelLine={false}
                            >
                              {productsStats.by_location.slice(0, 7).map((_, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [`${value} unidades`, '']} />
                          </RechartsPie>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Ranking de locales */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-slate-900 mb-4">Ranking de Locales</h3>
                      <div className="space-y-3 max-h-72 overflow-y-auto">
                        {productsStats.by_location.map((loc, idx) => {
                          const maxQty = productsStats.by_location[0]?.total_quantity || 1;
                          const percentage = (loc.total_quantity / maxQty) * 100;
                          return (
                            <div key={loc.location_id} className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="flex items-center gap-2">
                                  <span className="w-5 h-5 rounded text-xs font-bold flex items-center justify-center"
                                    style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] + '20', color: CHART_COLORS[idx % CHART_COLORS.length] }}>
                                    {idx + 1}
                                  </span>
                                  <span className="font-medium">{loc.location_name}</span>
                                </span>
                                <span className="font-bold">{loc.total_quantity}</span>
                              </div>
                              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{ 
                                    width: `${percentage}%`,
                                    backgroundColor: CHART_COLORS[idx % CHART_COLORS.length]
                                  }}
                                />
                              </div>
                              <p className="text-xs text-slate-500">{loc.order_count} pedidos</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab: Tendencia */}
                <TabsContent value="trend" className="mt-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-slate-900 mb-4">Tendencia Diaria de Pedidos</h3>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={productsStats.daily_trend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 10 }}
                            tickFormatter={(value) => {
                              const d = new Date(value);
                              return `${d.getDate()}/${d.getMonth() + 1}`;
                            }}
                          />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip 
                            labelFormatter={(value) => {
                              const d = new Date(value);
                              return d.toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' });
                            }}
                            formatter={(value, name) => [value, name === 'quantity' ? 'Unidades' : 'Pedidos']}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="quantity" 
                            stroke="#3b82f6" 
                            fill="#3b82f6" 
                            fillOpacity={0.3}
                            name="Unidades"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>

      {/* Tendencia de Pedidos (últimos 30 días) */}
      {orderTrends && orderTrends.trend.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-primary" />
              <div>
                <h2 className="text-lg font-heading font-bold text-slate-900">
                  Actividad de Pedidos (30 días)
                </h2>
                <p className="text-sm text-muted-foreground">
                  {orderTrends.totals.total_orders} pedidos • {orderTrends.totals.total_quantity.toLocaleString()} unidades
                </p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={orderTrends.trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => {
                      const d = new Date(value);
                      return `${d.getDate()}/${d.getMonth() + 1}`;
                    }}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    labelFormatter={(value) => {
                      const d = new Date(value);
                      return d.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="quantity" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Unidades pedidas"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="avg_7d" 
                    stroke="#22c55e" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name="Promedio 7 días"
                  />
                  <Legend />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;